import isNil from "lodash/isNil";
import type { FC } from "react";
import { useState } from "react";
import { useAuthContext } from "../../auth/AuthContext";
import type { PriceValue } from "../../misc/price";
import OptionPriceEditDialog from "../optionPrice/OptionPriceEditDialog";
import type { ProductOptionType } from "./ProductOptions";
import ProductOptions from "./ProductOptions";
import type { SelectedProductOption } from "./SelectedProductOption";

interface Props {
  readonly productId: string | undefined;
  readonly productDate: string;
  readonly selectedOptions: SelectedProductOption[];
  readonly availableOptions: ProductOptionType[];
  readonly readonly?: boolean;
  readonly onChange: (options: SelectedProductOption[]) => void;
}

const ProductOptionsWithPrice: FC<Props> = ({
  productId,
  productDate,
  selectedOptions,
  availableOptions,
  readonly,
  onChange,
}) => {
  const [optionToEdit, setOptionToEdit] = useState<SelectedProductOption | null>(null);
  const { context } = useAuthContext();

  const handleOptionPriceChange = (newPrice: PriceValue) => {
    if (!optionToEdit) {
      return;
    }
    const price = isNil(newPrice) ? undefined : newPrice;
    onChange(selectedOptions.map((o) => (o.id === optionToEdit.id ? { ...o, price } : o)));
    setOptionToEdit(null);
  };

  const canEditPrice =
    !readonly &&
    (context?.role === "ADMIN" ||
      (context?.role === "AGENT" &&
        (context?.customPrivileges.canEditPriceLists || !context?.customPrivileges.cannotChangeOptionPrices)));

  return (
    <>
      <ProductOptions
        productId={productId}
        selectedOptions={selectedOptions}
        availableOptions={availableOptions}
        readonly={readonly}
        onChange={onChange}
        onOptionHintDoubleClick={canEditPrice ? (o) => setOptionToEdit(o) : undefined}
      />
      {optionToEdit?.id && productId && (
        <OptionPriceEditDialog
          productId={productId}
          date={productDate}
          option={{ id: optionToEdit.id, price: optionToEdit.price }}
          onClose={() => setOptionToEdit(null)}
          onChange={handleOptionPriceChange}
        />
      )}
    </>
  );
};

export default ProductOptionsWithPrice;
